// frontend/src/hooks/useUserTier.js
// RUTHLESS VOICE FIX: Hook to fetch and track user's subscription tier
// Used by SimpleRecordingContext to enforce correct tier-based recording limits

import { useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook to fetch and track user's subscription tier
 * @returns {Object} { tier, loading, error, refetch }
 */
export const useUserTier = () => {
  const { currentUser } = useAuth();
  const [tier, setTier] = useState('free'); // Default to free tier
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tier from backend
  const fetchTier = useCallback(async () => {
    if (!currentUser) {
      setTier('free');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      const response = await axios.get('/api/user/tier', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const fetchedTier = response.data?.tier || 'free';
      console.log(`[useUserTier] Fetched tier: ${fetchedTier}`);

      setTier(fetchedTier);
      setError(null);
    } catch (err) {
      console.error('[useUserTier] Failed to fetch tier:', err);

      // FAIL SAFE: Default to free tier if fetch fails
      // Better to be conservative than to allow unlimited recording
      setTier('free');
      setError(err.response?.data?.error || 'Failed to fetch tier');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load tier on mount and when user changes
  useEffect(() => {
    fetchTier();
  }, [fetchTier]);

  return {
    tier,
    loading,
    error,
    refetch: fetchTier
  };
};
